import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'

interface Category {
  id: string
  code: string
  display_name_cn: string
  display_name_en: string
  tasks: Task[]
}

interface Task {
  id: string
  category_code: string
  name: string
  counting_rule: string
  is_active: boolean
  deleted_at: string | null
}

interface Member {
  id: string
  display_name: string
  role: string
  region_code: string
  group_name: string
  is_active: boolean
  account_status: string
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function daysDiff(dateStr: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(dateStr)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}

interface WorkLogFormProps {
  onSubmitSuccess?: () => void
}

export function WorkLogForm({ onSubmitSuccess }: WorkLogFormProps) {
  const { accessToken, user } = useAuth()
  const { t } = useLang()
  const [open, setOpen] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [members, setMembers] = useState<Member[]>([])

  const [categoryCode, setCategoryCode] = useState('')
  const [taskId, setTaskId] = useState('')
  const [taskName, setTaskName] = useState('')
  const [workDate, setWorkDate] = useState(todayStr())
  const [volume, setVolume] = useState('0')
  const [hours, setHours] = useState('')
  const [note, setNote] = useState('')
  const [registrantId, setRegistrantId] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const panelRef = useRef<HTMLDivElement>(null)

  const fetchData = useCallback(async () => {
    if (!accessToken) return
    const headers = { Authorization: `Bearer ${accessToken}` }
    const [catRes, memRes] = await Promise.all([
      fetch('/api/categories', { headers }),
      fetch('/api/members', { headers }),
    ])
    if (catRes.ok) {
      const catData: { data?: Category[] } = await catRes.json()
      setCategories(catData.data ?? [])
    }
    if (memRes.ok) {
      const data = await memRes.json()
      const list: Member[] = data.members ?? []
      // Filter members based on current user's role (R-33 frontend enforcement)
      setMembers(list.filter((m) => {
        if (m.role === 'admin' || !m.is_active || m.account_status !== 'active') return false
        if (!user) return false
        if (user.role === 'member') return m.id === user.id
        if (user.role === 'manager') {
          if (m.region_code !== user.region_code) return false
          if (user.visible_group_scope !== null && !user.visible_group_scope.includes(m.group_name)) return false
        }
        return true
      }))
    }
  }, [accessToken])

  useEffect(() => {
    if (open) fetchData()
  }, [open, fetchData])

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const selectedCategory = categories.find((c) => c.code === categoryCode)
  const isOthers = categoryCode === 'others'
  const activeTasks = selectedCategory?.tasks.filter((t) => t.is_active && !t.deleted_at) ?? []
  const selectedTask = activeTasks.find((t) => t.id === taskId)

  function reset() {
    setCategoryCode('')
    setTaskId('')
    setTaskName('')
    setWorkDate(todayStr())
    setVolume('0')
    setHours('')
    setNote('')
    setRegistrantId('')
    setError('')
    setSuccess(false)
  }

  async function handleSubmit(confirmed = false) {
    setError('')

    if (!categoryCode) { setError(t('wlFormSelectCategory')); return }
    if (!isOthers && !taskId) { setError(t('wlFormSelectTask')); return }
    if (!registrantId) { setError(t('wlFormSelectRegistrant')); return }
    if (!hours.trim()) { setError(t('wlFormHours')); return }

    const diff = daysDiff(workDate)
    if (!confirmed && diff > 7) {
      if (!window.confirm(t('wlFormDateFuture'))) return
    }
    if (!confirmed && diff < -90) {
      if (!window.confirm(t('wlFormDateOld'))) return
    }

    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        member_id: registrantId,
        work_date: workDate,
        category_code: categoryCode,
        volume: parseInt(volume, 10) || 0,
        hours: hours.trim(),
        note,
      }
      if (isOthers) {
        body.task_name = taskName.trim() || undefined
      } else {
        body.task_id = taskId
      }

      const res = await fetch('/api/work-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const data = await res.json()
        setError(data?.error?.message ?? t('errorGeneric'))
        return
      }

      setSuccess(true)
      reset()
      onSubmitSuccess?.()
      setTimeout(() => setSuccess(false), 3000)
    } catch {
      setError(t('errorGeneric'))
    } finally {
      setSubmitting(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '4px 6px',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    fontSize: '13px',
    boxSizing: 'border-box',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '11px',
    color: 'var(--text-secondary)',
    marginBottom: '2px',
  }
  const rowStyle: React.CSSProperties = { marginBottom: '8px' }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) reset() }}
        style={{
          padding: '4px 10px',
          borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.4)',
          background: open ? 'rgba(255,255,255,0.25)' : 'transparent',
          color: 'var(--nav-text)',
          cursor: 'pointer',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        }}
      >
        {t('wlFormOpen')}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '32px',
            right: 0,
            width: '320px',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            padding: '16px',
            zIndex: 1000,
            color: 'var(--text-primary)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
            {t('wlFormTitle')}
          </div>

          {/* Category */}
          <div style={rowStyle}>
            <label style={labelStyle}>{t('wlFormCategory')}</label>
            <select
              value={categoryCode}
              onChange={(e) => { setCategoryCode(e.target.value); setTaskId(''); setTaskName('') }}
              style={inputStyle}
            >
              <option value="">{t('wlFormSelectCategory')}</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.display_name_en}
                </option>
              ))}
            </select>
          </div>

          {/* Task */}
          {categoryCode && (
            <div style={rowStyle}>
              <label style={labelStyle}>{t('wlFormTask')}</label>
              {isOthers ? (
                <input
                  type="text"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder={t('wlFormTaskPlaceholder')}
                  style={inputStyle}
                />
              ) : (
                <select
                  value={taskId}
                  onChange={(e) => setTaskId(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">{t('wlFormSelectTask')}</option>
                  {activeTasks.map((task) => (
                    <option key={task.id} value={task.id}>{task.name}</option>
                  ))}
                </select>
              )}
              {selectedTask && (
                <div style={{ fontSize: '11px', color: 'var(--info)', marginTop: '2px' }}>
                  {t('wlFormCountingRule').replace('{rule}', selectedTask.counting_rule)}
                </div>
              )}
            </div>
          )}

          {/* Registrant */}
          <div style={rowStyle}>
            <label style={labelStyle}>{t('wlFormRegistrant')}</label>
            <select
              value={registrantId}
              onChange={(e) => setRegistrantId(e.target.value)}
              style={inputStyle}
            >
              <option value="">{t('wlFormSelectRegistrant')}</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.display_name}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div style={rowStyle}>
            <label style={labelStyle}>{t('wlFormDate')}</label>
            <input
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Volume */}
          <div style={rowStyle}>
            <label style={labelStyle}>{t('wlFormVolume')}</label>
            <input
              type="number"
              min="0"
              step="1"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Hours */}
          <div style={rowStyle}>
            <label style={labelStyle}>{t('wlFormHours')}</label>
            <input
              type="text"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="1.5h / 90m"
              style={inputStyle}
            />
          </div>

          {/* Note */}
          <div style={rowStyle}>
            <label style={labelStyle}>{t('wlFormNote')}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '8px' }}>{error}</div>
          )}
          {success && (
            <div style={{ color: 'var(--success)', fontSize: '12px', marginBottom: '8px' }}>{t('wlFormSuccess')}</div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setOpen(false); reset() }}
              style={{
                padding: '5px 12px',
                borderRadius: '4px',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {t('wlFormCancel')}
            </button>
            <button
              onClick={() => handleSubmit()}
              disabled={submitting}
              style={{
                padding: '5px 12px',
                borderRadius: '4px',
                border: 'none',
                background: 'var(--accent)',
                color: 'var(--accent-contrast)',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? t('loading') : t('wlFormSubmit')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
